import { Input, BlobSource, UrlSource, ALL_FORMATS } from 'mediabunny'

const MIN_FPS = 15
const MAX_FPS = 60

// Forcing every export to a flat 30fps regardless of what the source actually is (24/25/50/60fps
// are all common) makes the seek-per-output-frame export loop repeatedly land on the same decoded
// source frame at uneven intervals - the kind of irregular frame duplication that reads as visible
// judder/stutter rather than a deliberate, evenly-paced frame rate change. Reading the source's own
// average frame rate straight from its container (no playback needed) lets the export match it.
// averageBitrate comes back from the exact same packet scan (see PacketStats) - reading both here
// means only one probe is needed instead of two.
export const probeSourceMediaStats = async (file, url) => {
    let input = null
    try {
        const source = file ? new BlobSource(file) : new UrlSource(url)
        input = new Input({ formats: ALL_FORMATS, source })
        const track = await input.getPrimaryVideoTrack()
        if (!track) return { fps: null, bitrate: null }

        const stats = await track.computePacketStats(100)
        const fps = stats.averagePacketRate && isFinite(stats.averagePacketRate)
            ? Math.min(MAX_FPS, Math.max(MIN_FPS, Math.round(stats.averagePacketRate)))
            : null
        const bitrate = stats.averageBitrate && isFinite(stats.averageBitrate) ? Math.round(stats.averageBitrate) : null

        return { fps, bitrate }
    } catch (e) {
        return { fps: null, bitrate: null }
    } finally {
        if (input) input.dispose()
    }
}

const TARGET_CEILING_FPS = 30

// Matching the source exactly is only actually *necessary* to avoid judder when downsampling to a
// rate that doesn't evenly divide the source (24/25fps forced to 30fps is the classic case, since
// 30/24 and 30/25 aren't whole numbers). A 50 or 60fps source doesn't have that problem - decimating
// it evenly down to 25 or 30fps is judder-free and cuts the number of frames the export loop has to
// seek/render/encode in half, for no visible difference in typical social-video playback.
export const chooseExportFps = (sourceFps) => {
    if (!sourceFps) return TARGET_CEILING_FPS
    if (sourceFps <= TARGET_CEILING_FPS) return sourceFps
    const factor = Math.ceil(sourceFps / TARGET_CEILING_FPS)
    return Math.round(sourceFps / factor)
}
